/**
 * The real wiring — where `addon-benchmark`'s regia calls
 * `addon-post-analysis` through `ctx.api`, the ONLY door between two addons.
 *
 * This is the answer to "how does benchmark call post-analysis": ONE cap
 * call, `pipelineAnalytics.runReplayFrameProcessor.mutate(...)`, carrying
 * every successful frame's `FrameResult` plus the run's zones — nothing
 * else crosses. Inference itself (`pipelineExecutor.runPipeline`) is the
 * SAME cap production dispatch uses, pinned to `nodeId`.
 *
 * ## Fetching source bytes is injected, and can REFUSE
 *
 * `orchestrateReplayRun` takes `fetchSourceBytes`, which resolves to
 * {@link FetchSourceBytesResult} — `ok` with the bytes AND the wall-clock (or
 * upload-offset) instant byte 0 represents, or a named `refused`. The refusal
 * exists because "the recording window has no segments" is a RESULT this
 * whole plan exists to make legible, not a generic exception to swallow: a
 * `recording` source can fail for reasons an operator needs named
 * (`no-recording`, `no-footage`, `window-spans-multiple-segments`), the same
 * discipline `readPreflight`/`planReplayFrames` already apply to the other
 * two ways a run can be unrunnable.
 *
 * `startMs` matters because a recorder window read anchors on the nearest
 * keyframe AT OR BEFORE the requested `fromMs`
 * (`readGopWindowRange`/`fragmentsInWindow` in `addon-pipeline`'s recorder) —
 * the fetched bytes routinely start a little earlier than the window asked
 * for. `-ss` is computed against THAT instant, never against `source.fromMs`
 * directly: doing the latter would seek into the wrong content silently,
 * exactly the kind of bug a byte-range read invites when the caller assumes
 * "the bytes start where I asked" instead of "the bytes start where the
 * nearest keyframe was". The `upload` source has no such gap — its bytes are
 * the whole clip from t=0 — so it reports `startMs: 0` and the same formula
 * degrades to today's behaviour unchanged.
 */
import type { AddonApi, CameraPipelineConfig, IScopedLogger } from '@camstack/types'
import { buildReplayPlan, readPreflight, type ReplayOverlay } from './replay-plan.js'
import { planReplayFrames, type ReplaySource } from './replay-source.js'
import { buildReplaySequenceArgs, ReplayDecoder } from './replay-decode.js'
import { splitMjpegStream } from './mjpeg-split.js'
import { runReplay, type ReplayFrame, type ReplayVerdict } from './replay-run.js'
import { ReplayStore, type StoredReplayRun } from './replay-store.js'

export type OrchestrateReplayRunResult =
  | { readonly kind: 'ran'; readonly replayRunId: string; readonly verdict: ReplayVerdict }
  | { readonly kind: 'refused'; readonly reason: string }

/**
 * The outcome of fetching a `ReplaySource`'s raw bytes.
 *
 * `startMs` is the wall-clock (recording) or clip-offset (upload) instant
 * byte 0 of `bytes` represents — NOT necessarily `source.fromMs`. See the
 * file header for why the gap matters.
 *
 * `refused` carries a human-readable `reason` naming WHY there is nothing to
 * decode (`no-recording`, `no-footage`, `window-spans-multiple-segments`,
 * `clip-not-found`, ...) — the caller reports it exactly like a preflight or
 * frame-plan refusal, never as a generic thrown error.
 */
export type FetchSourceBytesResult =
  | { readonly kind: 'ok'; readonly bytes: Uint8Array; readonly startMs: number }
  | { readonly kind: 'refused'; readonly reason: string }

export interface OrchestrateReplayRunInput {
  readonly deviceId: number
  readonly nodeId: string
  readonly source: ReplaySource
  readonly overlay: ReplayOverlay
  readonly fps: number
  readonly maxFrames: number
  readonly maxWidth: number
  readonly deviceKey?: string
}

/** Everything this orchestrator needs from the outside world, injected —
 *  keeps it unit-testable without a real hub, and keeps the `ctx.api`
 *  surface it touches exactly enumerable (two calls: `pipelineExecutor`,
 *  `pipelineAnalytics`). */
export interface ReplayWiringDeps {
  readonly api: Pick<AddonApi, 'pipelineExecutor' | 'pipelineAnalytics'>
  readonly logger: IScopedLogger
  readonly decoder: ReplayDecoder
  readonly store: ReplayStore
  readonly ffmpegPath: string
  /** Resolve the camera's RESOLVED step tree — the replay's baseline before
   *  any overlay. `pipelineOrchestrator.resolvePipeline` in production; a
   *  fixture in tests. */
  readonly resolveBaseTree: (deviceId: number) => Promise<CameraPipelineConfig>
  /** Fetch the source clip's raw bytes for the ffmpeg decode step — see the
   *  file header for the refusal contract and why `startMs` is not always
   *  `source.fromMs`. */
  readonly fetchSourceBytes: (source: ReplaySource) => Promise<FetchSourceBytesResult>
  readonly now?: () => number
  readonly newId?: () => string
}

function clipIdOf(source: ReplaySource): string {
  return source.kind === 'upload' ? source.clipId : `recording:${source.deviceId}:${source.fromMs}`
}

/**
 * The end-to-end regia: resolve the plan, preflight it, plan the frames,
 * decode, run, store. Every cross-addon call is named in the file header —
 * nothing else reaches out.
 */
export async function orchestrateReplayRun(
  deps: ReplayWiringDeps,
  input: OrchestrateReplayRunInput,
): Promise<OrchestrateReplayRunResult> {
  const tags = { deviceId: input.deviceId }
  const base = await deps.resolveBaseTree(input.deviceId)
  const plan = buildReplayPlan(base, input.overlay)
  if (plan.ignoredOverlays.length > 0) {
    deps.logger.warn('replay: overlay named a step the resolved tree does not run', {
      tags,
      meta: { ignoredOverlays: plan.ignoredOverlays },
    })
  }

  const preflight = await deps.api.pipelineExecutor.validatePipeline.query({
    steps: [...plan.steps],
  })
  const preflightVerdict = readPreflight(preflight)
  if (preflightVerdict.kind === 'refused') {
    deps.logger.warn('replay: run refused at preflight', {
      tags,
      meta: { reason: preflightVerdict.reason, detail: preflightVerdict.detail },
    })
    return { kind: 'refused', reason: `${preflightVerdict.reason}: ${preflightVerdict.detail}` }
  }

  const framePlan = planReplayFrames(input.source, input.fps, input.maxFrames)
  if (framePlan.kind === 'refused') {
    deps.logger.warn('replay: run refused — empty window', { tags })
    return { kind: 'refused', reason: framePlan.reason }
  }
  if (framePlan.truncated) {
    deps.logger.warn('replay: frame plan truncated by maxFrames', {
      tags,
      meta: { maxFrames: input.maxFrames, planned: framePlan.instants.length },
    })
  }

  const sourceFetch = await deps.fetchSourceBytes(input.source)
  if (sourceFetch.kind === 'refused') {
    deps.logger.warn('replay: run refused — could not fetch source bytes', {
      tags,
      meta: { reason: sourceFetch.reason },
    })
    return { kind: 'refused', reason: sourceFetch.reason }
  }
  const sourceBytes = sourceFetch.bytes
  const first = framePlan.instants[0] ?? 0
  const last = framePlan.instants[framePlan.instants.length - 1] ?? first
  // Anchored on where the FETCHED bytes actually start, not on
  // `source.fromMs` — see the file header. For `upload`, `sourceFetch.startMs`
  // is 0 and this degrades to the original `first / 1000`.
  const startSec = (first - sourceFetch.startMs) / 1000
  const durationSec = Math.max(1 / input.fps, (last - first) / 1000 + 1 / input.fps)
  const args = buildReplaySequenceArgs({
    startSec,
    durationSec,
    fps: input.fps,
    maxWidth: input.maxWidth,
  })
  const mjpeg = await deps.decoder.decodeSequence(args, sourceBytes)
  const jpegFrames = splitMjpegStream(mjpeg)

  const frames: ReplayFrame[] = []
  for (let i = 0; i < framePlan.instants.length; i++) {
    const timestamp = framePlan.instants[i]
    const image = jpegFrames[i]
    if (timestamp === undefined || image === undefined) continue
    frames.push({ timestamp, image })
  }
  if (frames.length < framePlan.instants.length) {
    deps.logger.warn('replay: decode produced fewer frames than planned', {
      tags,
      meta: { planned: framePlan.instants.length, decoded: frames.length },
    })
  }

  const verdict = await runReplay(
    {
      deviceId: input.deviceId,
      nodeId: input.nodeId,
      runPipeline: async (call) =>
        deps.api.pipelineExecutor.runPipeline.mutate({
          nodeId: call.nodeId,
          steps: [...plan.steps],
          // Copy into a fresh `Uint8Array<ArrayBuffer>` — `mjpeg-split.ts`
          // hands back a `Buffer` view (`Uint8Array<ArrayBufferLike>`), and
          // the generated cap input is typed against the narrower
          // `ArrayBuffer` backing.
          image: new Uint8Array(call.image),
          deviceId: input.deviceId,
          ...(input.deviceKey !== undefined ? { deviceKey: input.deviceKey } : {}),
        }),
      // THE cross-addon call this whole track exists for: the FrameProcessor
      // pass lives in `addon-post-analysis`, reached only through this cap
      // method — see the file header.
      runTracking: async (tracked) =>
        deps.api.pipelineAnalytics.runReplayFrameProcessor.mutate({
          deviceId: input.deviceId,
          source: 'pipeline',
          frames: [...tracked],
        }),
      logger: deps.logger,
    },
    frames,
  )

  const now = deps.now ?? Date.now
  const newId =
    deps.newId ??
    ((): string => `replay-${now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`)
  const replayRunId = newId()
  const record: StoredReplayRun = {
    id: replayRunId,
    deviceId: input.deviceId,
    nodeId: input.nodeId,
    clipId: clipIdOf(input.source),
    createdAtMs: now(),
    source: input.source,
    plan,
    verdict,
  }
  await deps.store.save(record)

  return { kind: 'ran', replayRunId, verdict }
}
